import React, { useMemo, useState } from "react";
import { useFormikContext, Field } from "formik";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import moment from "moment";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import DeliveryDateField from "../../../views/shopping/Cart/SimpleCheckout/DeliveryDateField";
import Input from "../../../components/form/Input";
import { useCustomFields } from "../../../contexts/customFields";
import { getWO } from "../../inventory/requests/orders/RequestRow";
import DateTimePicker from "../../../components/ui/DateTimePicker";
import { usePermissions } from "../../../contexts/permissions";
import InventoryItemAddress from "../../../views/shopping/Cart/InventoryItemAddress";
import Requestor from "../../inventory/requests/orders/Requestor";
import { isStartDateGreaterEndDate } from "./WizardCheckout/Step2";
// import ResourcesField from "./ResourcesField";
import CreatedByEmail from "./CreatedByEmail";
import DispositionField from "src/views/shopping/Cart/DispositionField";
import useRoles from "../../../hooks/user/useRoles";
import labels from "../../../constants/displayFields";
import PalletsBuiltField from "src/views/inventory/requests/orders/PalletsBuiltField";
import useMinDate from "src/hooks/useMinDate";
import useBlackoutDates from "src/hooks/shopping/useBlackoutDates";
import { getRestrictedDays, isDateRestricted } from "src/views/inventory/requests/orders/utils";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";

export default function CartForm({
  projectId,
  inventoryRequest,
  email,
  user: inventoryRequestUser,
  workOrderNumber,
  revisionNumber,
  children,
  afterComment,
  showAllConfigurableFields = false, // Only show fields 4 & 5 in edit modal, not checkout
  showPickingNotesWhenPresentOnly = false,
  disabled = false,
  fieldDisabled = false,
}) {
  const [openPalletsBuiltCountModal, setOpenPalletsBuiltCountModal] = useState(false);
  const [openStartDate, setOpenStartDate] = useState(false);
  const [openEndDate, setOpenEndDate] = useState(false);
  const { values, setFieldValue } = useFormikContext();
  const { user } = usePermissions();
  const [display] = useCustomFields({ projectId });
  const isAdminOrManager = useRoles(["admin", "manager"]);
  const commentsName = user.company.settings.inventory_request_comments_name;
  const { settings } = user.company;
  const showConfigurableField1 = display.ir_configurable_field_1;
  const showConfigurableField2 = display.ir_configurable_field_2;
  const showConfigurableField3 = display.ir_configurable_field_3;
  // Fields 4 & 5 are only shown to admins/managers in edit modal
  const showConfigurableField4 = display.ir_configurable_field_4 && isAdminOrManager;
  const showConfigurableField5 = display.ir_configurable_field_5 && isAdminOrManager;

  const minDate = useMinDate();
  const { blackoutDates } = useBlackoutDates();
  const restrictedDays = isAdminOrManager ? [] : getRestrictedDays(blackoutDates);

  const shouldDisableDate = date => {
    const formattedDate = date.format("YYYY-MM-DD");
    return restrictedDays.some(day => day.date === formattedDate);
  };

  const isRestrictedStartDate =
    !isAdminOrManager &&
    values.startDate &&
    isDateRestricted(blackoutDates, moment(values.startDate).format("YYYY-MM-DD"));

  const CustomDay = props => {
    const { day, ...other } = props;
    const isDisabled = shouldDisableDate(day);
    return (
      <PickersDay
        {...other}
        day={day}
        sx={{
          ...(isDisabled && {
            color: "red !important",
            textDecoration: "line-through",
            backgroundColor: "transparent",
          }),
        }}
      />
    );
  };

  return (
    <>
      <Card>
        <CardContent>
          <Grid container spacing={1}>
            {display.ir_location && (
              <Grid size={{ xs: 12, md: 12 }}>
                <InventoryItemAddress
                  inventoryItem={{ location: values.location }}
                  onChangeField={() => {}}
                  onChange={v => {
                    setFieldValue("location", v.location);
                  }}
                  disabled={disabled || fieldDisabled}
                />
              </Grid>
            )}
            {showConfigurableField1 && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Input
                  name="configurableField1"
                  label={
                    settings.inventory_request_configurable_field_1_name || "Configurable Field 1"
                  }
                  required
                  disabled={disabled || fieldDisabled}
                />
              </Grid>
            )}
            {showConfigurableField2 && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Input
                  name="configurableField2"
                  label={
                    settings.inventory_request_configurable_field_2_name || "Configurable Field 2"
                  }
                  required
                  disabled={disabled || fieldDisabled}
                />
              </Grid>
            )}
            {display.ir_start_date && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Field name="startDate">
                  {({ field, form, meta }) => {
                    const errorMessage = meta.error || form.errors[field.name];
                    const error = (meta.touched || form.submitCount > 0) && errorMessage;

                    if (isAdminOrManager) {
                      const defaultTime = values.startTime || "00:00:00";
                      const defaultValue = values.startDate
                        ? `${values.startDate}T${defaultTime}`
                        : "";
                      return (
                        <DateTimePicker
                          label="Start Date and Time"
                          minDate={isAdminOrManager ? undefined : minDate}
                          error={error}
                          helperText={errorMessage}
                          defaultValue={defaultValue}
                          disabled={disabled}
                          onChange={({ date, time }) => {
                            form.setFieldValue("startDate", date);
                            form.setFieldValue("startTime", time);
                          }}
                        />
                      );
                    }

                    const dateValue = values.startDate ? moment(values.startDate) : null;
                    const displayValue =
                      values.startDate && values.startTime && values.startTime !== "00:00:00"
                        ? `${moment(values.startDate).format("MM/DD/YYYY")} ${moment(
                            values.startTime,
                            "HH:mm:ss"
                          ).format("hh:mm A")}`
                        : values.startDate
                        ? moment(values.startDate).format("MM/DD/YYYY")
                        : "";

                    const hasError = error || isRestrictedStartDate;
                    const helperText = isRestrictedStartDate
                      ? "Selected date has been marked as unavailable"
                      : errorMessage;

                    return (
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                          open={openStartDate}
                          onOpen={() => setOpenStartDate(true)}
                          onClose={() => setOpenStartDate(false)}
                          label="Start Date"
                          minDate={minDate}
                          value={dateValue}
                          disabled={disabled}
                          shouldDisableDate={shouldDisableDate}
                          slots={{ day: CustomDay }}
                          onChange={date => {
                            const dateFormat = "yyyy-MM-DD";
                            form.setFieldValue("startDate", moment(date).format(dateFormat));
                            form.setFieldValue("startTime", "00:00:00");
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              variant: "outlined",
                              error: hasError,
                              helperText: helperText,
                              autoComplete: "off",
                              disabled: disabled,
                              onClick: () => !disabled && setOpenStartDate(true),
                            },
                          }}
                        />
                      </LocalizationProvider>
                    );
                  }}
                </Field>
              </Grid>
            )}
            {display.ir_end_date && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Field name="endDate">
                  {({ field, form, meta }) => {
                    const errorMessage = meta.error || form.errors[field.name];
                    const error = (meta.touched || form.submitCount > 0) && errorMessage;

                    if (isAdminOrManager) {
                      const defaultTime = values.endTime || "00:00:00";
                      const defaultValue = values.endDate ? `${values.endDate}T${defaultTime}` : "";
                      return (
                        <DateTimePicker
                          label="End Date and Time"
                          minDate={
                            isAdminOrManager
                              ? undefined
                              : values.startDate
                              ? `${values.startDate}T${values.startTime}`
                              : minDate
                          }
                          error={error || isStartDateGreaterEndDate(values)}
                          helperText={errorMessage}
                          defaultValue={defaultValue}
                          disabled={disabled}
                          onChange={({ date, time }) => {
                            form.setFieldValue("endDate", date);
                            form.setFieldValue("endTime", time);
                          }}
                        />
                      );
                    }

                    const dateValue = values.endDate ? moment(values.endDate) : null;

                    const displayValue =
                      values.endDate && values.endTime && values.endTime !== "00:00:00"
                        ? `${moment(values.endDate).format("MM/DD/YYYY")} ${moment(
                            values.endTime,
                            "HH:mm:ss"
                          ).format("hh:mm A")}`
                        : values.endDate
                        ? moment(values.endDate).format("MM/DD/YYYY")
                        : "";

                    const isRestrictedEndDate =
                      !isAdminOrManager &&
                      values.endDate &&
                      isDateRestricted(blackoutDates, moment(values.endDate).format("YYYY-MM-DD"));

                    const hasError =
                      error || isRestrictedEndDate || isStartDateGreaterEndDate(values);
                    const helperText = isRestrictedEndDate
                      ? "Selected date has been marked as unavailable"
                      : errorMessage;

                    return (
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                          open={openEndDate}
                          onOpen={() => setOpenEndDate(true)}
                          onClose={() => setOpenEndDate(false)}
                          label="End Date"
                          value={dateValue}
                          disabled={disabled}
                          minDate={
                            isAdminOrManager
                              ? undefined
                              : values.startDate
                              ? moment(values.startDate)
                              : moment(minDate)
                          }
                          slots={{ day: CustomDay }}
                          shouldDisableDate={shouldDisableDate}
                          onChange={date => {
                            const formattedDate = date ? date.format("YYYY-MM-DD") : "";
                            form.setFieldValue("endDate", formattedDate);
                            form.setFieldValue("endTime", "00:00:00");
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              variant: "outlined",
                              error: hasError,
                              helperText: helperText,
                              autoComplete: "off",
                              disabled: disabled,
                              onClick: () => !disabled && setOpenEndDate(true),
                            },
                          }}
                        />
                      </LocalizationProvider>
                    );
                  }}
                </Field>
              </Grid>
            )}
            {display.ir_delivery_date && (
              <Grid size={{ xs: 12, md: 6 }}>
                <DeliveryDateField
                  minDate={isAdminOrManager ? undefined : minDate}
                  disabled={disabled || fieldDisabled}
                />
              </Grid>
            )}
            {display.ir_email && (
              <Grid size={{ xs: 12, md: 6 }}>
                <CreatedByEmail email={email} user={inventoryRequestUser} disabled={disabled} />
              </Grid>
            )}
            {showConfigurableField3 && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Input
                  name="configurableField3"
                  label={
                    settings.inventory_request_configurable_field_3_name || "Configurable Field 3"
                  }
                  required
                  disabled={disabled || fieldDisabled}
                />
              </Grid>
            )}
            {showAllConfigurableFields && showConfigurableField4 && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Input
                  name="configurableField4"
                  label={
                    settings.inventory_request_configurable_field_4_name || "Configurable Field 4"
                  }
                  disabled={disabled || fieldDisabled}
                />
              </Grid>
            )}
            {showAllConfigurableFields && showConfigurableField5 && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Input
                  name="configurableField5"
                  label={
                    settings.inventory_request_configurable_field_5_name || "Configurable Field 5"
                  }
                  disabled={disabled || fieldDisabled}
                />
              </Grid>
            )}
            {display.ir_contact_name && (
              <Grid size={6}>
                <Input name="contactName" label="Contact Name" disabled={disabled} />
              </Grid>
            )}
            {display.ir_contact_phone_number && (
              <Grid size={6}>
                <Input name="contactPhoneNumber" label="Contact Phone Number" disabled={disabled} />
              </Grid>
            )}
            {display.ir_cc_emails && (
              <Grid size={12}>
                <Input
                  name="ccEmails"
                  label={labels.ir_cc_emails}
                  disabled={disabled || fieldDisabled}
                />
              </Grid>
            )}
            {display.ir_comment && (
              <Grid size={12}>
                <Input
                  type="text"
                  name="comment"
                  label={commentsName || labels.ir_comment}
                  disabled={disabled || fieldDisabled}
                />
              </Grid>
            )}
            {afterComment}
            {display.ir_disposition && (
              <Grid size={12}>
                <DispositionField
                  name="disposition"
                  disabled={disabled}
                  onChange={value => {
                    setFieldValue("disposition", value);
                    setFieldValue(
                      "inventoryItems",
                      values.inventoryItems.map(item => ({
                        ...item,
                        disposition: value,
                      }))
                    );
                  }}
                />
              </Grid>
            )}
            {display.ir_po_number && (
              <Grid size={{ xs: 12, md: workOrderNumber ? 6 : 12 }}>
                <Input name="poNumber" label="PO Number" disabled={disabled} />
              </Grid>
            )}
            {display.ir_picking_notes &&
              (!showPickingNotesWhenPresentOnly || values.pickingNotes?.trim()) && (
              <Grid size={12}>
                <Input name="pickingNotes" label={labels.ir_picking_notes} disabled={disabled} />
              </Grid>
            )}
            {/* Temporarily hide the resources section in this flow.
            {display.ir_resources && (
              <Grid size={12}>
                <ResourcesField disabled={disabled} />
              </Grid>
            )} */}
            {display.ir_designer && (
              <Grid size={12}>
                <Requestor disabled={disabled} />
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 6 }}>
              <PalletsBuiltField
                projectId={inventoryRequest.projectId}
                value={values.billingQuantities}
                open={openPalletsBuiltCountModal}
                setOpen={setOpenPalletsBuiltCountModal}
                disabled={disabled}
                onSubmit={billingQuantities => {
                  setFieldValue("billingQuantities", billingQuantities);
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      {children}
    </>
  );
}
